import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Lock, Heart } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../context/ThemeContext";

export default function Games() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [games, setGames] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [tab, setTab] = useState("all"); // "all" | "favourites"

  useEffect(() => {
    api.get("/games?status_filter=live").then(({ data }) => setGames(data));
  }, []);

  useEffect(() => {
    if (!user) { setFavs(new Set()); return; }
    api.get("/me/favourites").then(({ data }) => setFavs(new Set(data.game_ids || []))).catch(() => {});
  }, [user]);

  const toggleFav = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to save favourites");
      return;
    }
    const isFav = favs.has(id);
    // optimistic
    const next = new Set(favs);
    if (isFav) next.delete(id); else next.add(id);
    setFavs(next);
    try {
      if (isFav) await api.delete(`/me/favourites/${id}`);
      else await api.post(`/me/favourites/${id}`);
    } catch (err) {
      // rollback
      const rollback = new Set(favs);
      setFavs(rollback);
      toast.error(formatApiError(err));
    }
  };

  const filtered = tab === "favourites" ? games.filter((g) => favs.has(g.id)) : games;
  const pageClass = isDark
    ? "relative isolate min-h-screen overflow-x-clip bg-[#05070D] text-white"
    : "relative isolate min-h-screen overflow-x-clip bg-[#064D2B] text-white";
  const pageFieldStyle = {
    background: isDark
      ? "radial-gradient(circle at 80% 10%, rgba(47,111,165,0.18), transparent 18%), linear-gradient(120deg, #05070D, #071D17 48%, #05070D)"
      : "radial-gradient(circle at 84% 9%, rgba(255,245,157,0.22), transparent 16%), radial-gradient(circle at 12% 8%, rgba(125,211,252,0.14), transparent 18%), linear-gradient(120deg, rgba(4,72,39,0.98), rgba(14,117,58,0.96) 48%, rgba(3,58,36,0.98)), repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 54px, rgba(255,255,255,0.02) 54px 108px)",
  };
  const tabShellClass = isDark
    ? "mt-8 md:mt-10 inline-flex max-w-full p-1 rounded-full bg-[#081421] border border-white/10 shadow-sm"
    : "mt-8 md:mt-10 inline-flex max-w-full p-1 rounded-full bg-black/20 border border-white/15 shadow-sm backdrop-blur-sm";
  const cardClass = isDark
    ? "group relative min-w-0 rounded-2xl overflow-hidden border border-white/10 bg-[#081421] shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#2F6FA5]/50 hover:shadow-lg"
    : "group relative min-w-0 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#2F6FA5]/40 hover:shadow-lg";

  return (
    <div className={pageClass}>
      <div className="absolute inset-0 -z-20" style={pageFieldStyle} />
      {!isDark && (
        <>
          <div className="absolute inset-y-0 left-1/2 -z-10 w-px bg-white/25" />
          <div className="absolute left-1/2 top-48 -z-10 h-72 w-72 -translate-x-1/2 rounded-full border border-white/20" />
          <div className="absolute -left-20 top-32 -z-10 h-44 w-72 rounded-xl border border-white/18" />
          <div className="absolute -right-20 top-96 -z-10 h-44 w-72 rounded-xl border border-white/18" />
        </>
      )}
      <Navbar />
      <section className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 pt-8 sm:pt-12 md:pt-20 pb-6 sm:pb-8">
        <div className={`mb-4 text-xs uppercase tracking-[0.2em] ${isDark ? "text-[#2F6FA5]" : "text-emerald-100"}`}>Game library</div>
        <h1 className="font-display text-3xl font-black tracking-tighter min-[360px]:text-4xl md:text-7xl">Pick your favorite game.</h1>

        {/* Tab switcher */}
        <div className={tabShellClass} data-testid="library-tabs">
          <Tab active={tab === "all"} dark={isDark} onClick={() => setTab("all")} count={games.length} testid="tab-all">
            All games
          </Tab>
          <Tab active={tab === "favourites"} dark={isDark} onClick={() => setTab("favourites")} icon={Heart} count={favs.size} testid="tab-favourites">
            Favourites
          </Tab>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 pb-32 md:pb-24 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-5">
        {filtered.map((g) => {
          const playable = g.status === "live";
          const Wrapper = playable ? Link : "div";
          const props = playable ? { to: `/games/${g.id}` } : {};
          const isFav = favs.has(g.id);
          const bonusInfo = gameBonusInfo(g, user);
          return (
            <Wrapper
              key={g.id}
              data-testid={`game-card-${g.id}`}
              {...props}
              className={`${cardClass} ${
                playable ? "cursor-pointer" : "opacity-70"
              }`}
            >
              <div className={`relative aspect-[5/4] overflow-hidden ${isDark ? "bg-[#050B14]" : "bg-slate-100"}`}>
                {g.image_url ? (
                  <img
                    src={g.image_url}
                    alt={g.name}
                    className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-700 group-hover:scale-105 sm:object-cover sm:p-0"
                  />
                ) : null}
                <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? "from-[#081421]/80 via-transparent to-transparent" : "from-white/45 via-transparent to-transparent"}`} />
              </div>

              {/* Favourite heart */}
              <button
                onClick={(e) => toggleFav(e, g.id)}
                data-testid={`fav-${g.id}`}
                title={isFav ? "Remove from favourites" : "Add to favourites"}
                className={`absolute top-2.5 left-2.5 sm:top-4 sm:left-4 z-10 p-2 rounded-full backdrop-blur border transition-all ${
                  isFav
                    ? "bg-[#2F6FA5] border-[#2F6FA5] text-white"
                    : isDark
                      ? "bg-[#050B14]/90 border-white/15 text-slate-200 shadow-sm hover:bg-[#081421] hover:text-[#7AB8EA]"
                      : "bg-white/90 border-slate-200 text-slate-700 shadow-sm hover:bg-white hover:text-[#2F6FA5]"
                }`}
              >
                <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
              </button>

              {bonusInfo.percent > 0 && (
                <div
                  className={`absolute top-2.5 left-14 z-10 max-w-[calc(100%-4.5rem)] rounded-full border px-2.5 py-1 text-[10px] font-black tracking-[0.02em] shadow-sm backdrop-blur sm:top-4 sm:left-[4.25rem] sm:px-3 sm:text-xs ${
                    bonusInfo.kind === "signup"
                      ? isDark
                        ? "border-emerald-400/30 bg-emerald-400/15 text-emerald-100"
                        : "border-emerald-200 bg-emerald-50/95 text-emerald-700"
                      : isDark
                        ? "border-[#2F6FA5]/40 bg-[#2F6FA5]/25 text-[#BFE0FF]"
                        : "border-[#2F6FA5]/20 bg-[#E7F2FB]/95 text-[#2F6FA5]"
                  }`}
                  title={bonusInfo.kind === "signup" ? "First game load bonus" : "Regular game load bonus"}
                >
                  <span className="block whitespace-nowrap">{formatBonusPercent(bonusInfo.percent)}% bonus</span>
                </div>
              )}

              {!playable && (
                  <div className={`absolute top-2.5 right-2.5 sm:top-4 sm:right-4 px-2 sm:px-3 py-1 rounded-full backdrop-blur border text-[10px] sm:text-xs flex items-center gap-1.5 shadow-sm ${
                    isDark ? "border-white/15 bg-[#050B14]/90 text-slate-300" : "border-slate-200 bg-white/90 text-slate-600"
                  }`}>
                  <Lock className="w-3 h-3" /> Coming soon
                </div>
              )}
              <div className="relative p-3 sm:p-5">
                <h3 className={`font-display text-base sm:text-xl font-bold leading-tight mb-1 line-clamp-1 ${isDark ? "text-white" : "text-slate-950"}`}>{g.name}</h3>
                <p className={`hidden sm:block text-sm line-clamp-2 ${isDark ? "text-slate-300" : "text-slate-500"}`}>{g.description}</p>
                {playable && (
                  <div className="mt-2 sm:mt-4 flex items-center justify-between gap-2 text-xs">
                    <span className={isDark ? "text-slate-300" : "text-slate-500"}>{Number(g.rtp) > 0 ? `RTP ${g.rtp}%` : ""}</span>
                    <span className="text-[#2F6FA5] font-semibold whitespace-nowrap">Play -&gt;</span>
                  </div>
                )}
              </div>
            </Wrapper>
          );
        })}

        {filtered.length === 0 && tab === "favourites" && (
          <div className="col-span-full text-center py-24" data-testid="empty-favourites">
            <Heart className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="font-display text-2xl font-bold mb-2">No favourites yet</p>
            <p className="text-sm text-slate-500 mb-6">
              Tap the heart on any game to save it here for quick access.
            </p>
            <button
              onClick={() => setTab("all")}
              className="px-6 py-2.5 rounded-full bg-[#2F6FA5] text-white font-semibold hover:bg-[#4E91C8]"
            >
              Browse all games
            </button>
          </div>
        )}
        {filtered.length === 0 && tab === "all" && (
          <div className="col-span-full text-center text-slate-500 py-20">No games yet.</div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function gameBonusInfo(game, user) {
  const hasLoadedBefore = Boolean(user && (user.first_load_bonus_used || Number(user.approved_load_count || 0) > 0));
  const firstBonus = safeBonusPercent(game?.first_deposit_bonus_percent, 100);
  const regularBonus = safeBonusPercent(game?.regular_deposit_bonus_percent, 20);
  return hasLoadedBefore
    ? { kind: "regular", percent: regularBonus }
    : { kind: "signup", percent: firstBonus };
}

function safeBonusPercent(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

function formatBonusPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return Number.isInteger(number) ? String(number) : number.toFixed(1).replace(/\.0$/, "");
}

function Tab({ active, dark, onClick, icon: Icon, children, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
        active
          ? "bg-[#2F6FA5] text-white"
          : dark ? "text-slate-300 hover:text-white" : "text-emerald-50/85 hover:text-white"
      }`}
    >
      {Icon && <Icon className={`w-4 h-4 ${active && Icon === Heart ? "fill-current" : ""}`} />}
      {children}
      <span
        className={`ml-1 px-2 py-0.5 rounded-full text-[10px] tabular-nums ${
          active ? "bg-black/15 text-white" : dark ? "bg-white/10 text-slate-300" : "bg-white/15 text-white"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
